import React, { useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({ open, isOpen, onClose, title, children, footer, size = "md" }) => {
  const isShown = open !== undefined ? open : (isOpen !== undefined ? isOpen : false);
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();

  const widths = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };

  // Move focus into the modal on open, and restore it to whatever triggered
  // the modal once it closes.
  useEffect(() => {
    if (isShown) {
      previouslyFocusedRef.current = document.activeElement;

      const node = dialogRef.current;
      const focusTarget = node?.querySelector(FOCUSABLE_SELECTOR) || node;
      focusTarget?.focus();

      return () => {
        previouslyFocusedRef.current?.focus?.();
      };
    }
  }, [isShown]);

  // Close on Escape, and keep Tab focus cycling inside the modal while open.
  useEffect(() => {
    if (!isShown) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key === "Tab") {
        const node = dialogRef.current;
        if (!node) return;
        const focusable = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isShown, onClose]);

  if (!isShown || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in" data-lenis-prevent>
      <div className="absolute inset-0 bg-ink-950/40 dark:bg-ink-950/75 backdrop-blur-[2px]" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`relative w-full ${widths[size]} rounded-2xl bg-white dark:bg-ink-900 border border-transparent dark:border-ink-800 text-ink-900 dark:text-ink-100 shadow-popover animate-slide-up max-h-[90vh] flex flex-col`}
      >
        <div className="flex items-center justify-between border-b border-ink-100 dark:border-ink-800 px-5 py-4">
          <h3 id={titleId} className="text-base font-semibold text-ink-900 dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-ink-400 hover:bg-ink-100 dark:hover:bg-ink-800 hover:text-ink-600 dark:hover:text-ink-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5" data-lenis-prevent>{children}</div>
        {footer && <div className="shrink-0 border-t border-ink-100 dark:border-ink-800 px-5 py-4 flex justify-between">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
